import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';
import spaceyAvatar from '../../../assets/images/agent/spacey-2026-3d.svg';
import useMetaTags from '../../../hooks/useMetaTags';

const SUPPORT_EMAIL = 'hello@millionspaces.com';
const SUPPORT_PHONE_LABEL = '0117 811 811';
const SUPPORT_PHONE = '0117811811';

const TOPICS = [
  { id: 'all', label: 'All topics' },
  { id: 'booking', label: 'Booking a space' },
  { id: 'payments', label: 'Payments' },
  { id: 'cancellations', label: 'Cancellations' },
  { id: 'hosting', label: 'Hosting' },
  { id: 'account', label: 'Account & support' },
];

const FAQS = [
  {
    topic: 'booking',
    question: 'How do I book a space?',
    answer:
      'Once you find a space you like, select your preferred date and time, and follow the booking steps. You may need to sign in or create an account to complete the booking.',
  },
  {
    topic: 'booking',
    question: 'What happens after I make a booking?',
    answer: 'You’ll receive a confirmation email, and the space will be reserved for you.',
  },
  {
    topic: 'booking',
    question: 'Is MillionSpaces free to use?',
    answer: 'Browsing spaces is completely free. You only pay when you confirm a booking.',
  },
  {
    topic: 'payments',
    question: 'What are the available payment options?',
    answer: 'We offer both bank transfer and card payments.',
  },
  {
    topic: 'cancellations',
    question: 'How do I cancel my booking?',
    answer: 'Go to your profile > ‘My Bookings’ from the kebab menu click on cancel booking.',
  },
  {
    topic: 'hosting',
    question: 'How can I list my space on MillionSpaces?',
    answer:
      'Click on “Become a host” in the header and follow the simple listing process which will take only five minutes. You’ll need to add photos, descriptions, availability, and pricing.',
  },
  {
    topic: 'hosting',
    question: 'Are there any listing fees?',
    answer: 'MillionSpaces currently does not charge a listing fee. It is waived off for all our hosts.',
  },
  {
    topic: 'hosting',
    question: 'Can I integrate my booking calendar with MillionSpaces?',
    answer:
      "Currently, MillionSpaces only supports integration with Google Calendar. If you're using any other coworking management system or POS system, unfortunately, integration is not available at the moment. However, this feature is under development and will be available soon.",
  },
  {
    topic: 'account',
    question: 'What is MillionSpaces?',
    answer:
      'MillionSpaces is an online booking platform that helps users find, book, and manage various types of spaces - such as meeting rooms, virtual offices, co-working spaces, private offices, boardrooms, interview spaces, training, team building, photo & video shoot spaces across Sri Lanka.',
  },
  {
    topic: 'account',
    question: 'How can I contact customer support?',
    answer: `You can call the MillionSpaces hotline directly on ${SUPPORT_PHONE_LABEL}, or email us at ${SUPPORT_EMAIL}.`,
  },
];

const SEARCH_SUGGESTIONS = ['Cancel a booking', 'Payment options', 'List my space'];
const NAV_SCROLL_OFFSET = 88;

const filterFaqs = (searchQuery, topic = 'all') => {
  const terms = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return FAQS.filter((faq) => {
    const matchesTopic = topic === 'all' || faq.topic === topic;
    const haystack = `${faq.question} ${faq.answer}`.toLowerCase();
    return matchesTopic && terms.every((term) => haystack.includes(term));
  });
};

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 3h3l2 5-2.5 1.5a12 12 0 0 0 5.5 5.5L15 12.5l5 2v3.5a2 2 0 0 1-2.2 2A17 17 0 0 1 3 5.2 2 2 0 0 1 5 3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="m4 8 7.2 5a1.5 1.5 0 0 0 1.6 0L20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 12.5c0 3.9-3.6 7-8 7a9.6 9.6 0 0 1-2.7-.4L4 21l1.3-3.6A6.7 6.7 0 0 1 4 12.5c0-3.9 3.6-7 8-7s8 3.1 8 7Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="12.5" r="1" fill="currentColor" />
    <circle cx="12" cy="12.5" r="1" fill="currentColor" />
    <circle cx="15" cy="12.5" r="1" fill="currentColor" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h13m-5.5-5.5L18 12l-5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const openSpacey = () => {
  const launcher = document.querySelector('.chatbot-button');
  if (launcher) {
    launcher.click();
  }
};

const HelpAndSupport = () => {
  useMetaTags('default');

  const history = useHistory();
  const { country } = useParams();

  const [query, setQuery] = useState('');
  const [activeTopic, setActiveTopic] = useState('all');
  const [expandedQuestion, setExpandedQuestion] = useState(FAQS[0].question);
  const answersRef = useRef(null);

  const scrollToAnswers = useCallback(() => {
    if (!answersRef.current) return;
    const top = answersRef.current.getBoundingClientRect().top + window.pageYOffset - NAV_SCROLL_OFFSET;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  const applySearch = useCallback(
    (value, shouldScroll = false) => {
      setActiveTopic('all');
      setQuery(value);

      if (shouldScroll) {
        const matches = filterFaqs(value, 'all');
        setExpandedQuestion(matches[0]?.question || null);
        window.requestAnimationFrame(() => {
          scrollToAnswers();
        });
      }
    },
    [scrollToAnswers]
  );

  const submitSearch = useCallback(() => {
    if (!query.trim()) return;
    applySearch(query, true);
  }, [applySearch, query]);

  const visibleFaqs = useMemo(() => filterFaqs(query, activeTopic), [query, activeTopic]);

  // A question stays closed once the user closes it, but a stale selection left
  // behind by filtering falls back to the top result so the list is never blank.
  const openQuestion =
    expandedQuestion === null || visibleFaqs.some((faq) => faq.question === expandedQuestion)
      ? expandedQuestion
      : visibleFaqs[0]?.question || null;

  const topicsWithCount = useMemo(
    () =>
      TOPICS.map((topic) => ({
        ...topic,
        count: topic.id === 'all' ? FAQS.length : FAQS.filter((faq) => faq.topic === topic.id).length,
      })),
    []
  );

  const toggleQuestion = (question) => setExpandedQuestion(openQuestion === question ? null : question);

  const resetFilters = () => {
    setQuery('');
    setActiveTopic('all');
  };

  const channels = [
    {
      id: 'call',
      icon: <PhoneIcon />,
      title: 'Call the hotline',
      description: 'Talk to our team for urgent booking or space questions.',
      value: SUPPORT_PHONE_LABEL,
      href: `tel:${SUPPORT_PHONE}`,
    },
    {
      id: 'email',
      icon: <MailIcon />,
      title: 'Email us',
      description: 'Share the details and we’ll get back to you with a full answer.',
      value: SUPPORT_EMAIL,
      href: `mailto:${SUPPORT_EMAIL}`,
    },
    {
      id: 'chat',
      icon: <ChatIcon />,
      title: 'Chat with Spacey',
      description: 'Our assistant can find spaces and answer questions instantly.',
      value: 'Start a chat',
      onClick: openSpacey,
    },
  ];

  return (
    <div className="help-page">
      <Helmet>
        <title>Contact MillionSpaces - Book meeting rooms Sri Lanka</title>
        <meta
          name="description"
          content="Contact workspace booking platform MillionSpaces for more details on how to book flexible office spaces, coworking spaces, and meeting rooms in Sri Lanka."
        />
        <meta property="og:title" content="Contact MillionSpaces - Book meeting rooms Sri Lanka" />
        <meta
          property="og:description"
          content="Contact workspace booking platform MillionSpaces for more details on how to book flexible office spaces, coworking spaces, and meeting rooms in Sri Lanka."
        />
      </Helmet>

      <section className="help-hero">
        <span className="help-hero__glow help-hero__glow--left" />
        <span className="help-hero__glow help-hero__glow--right" />

        <div className="help-shell help-hero__inner">
          <div className="help-hero__copy">
            <span className="help-hero__eyebrow">Help centre</span>
            <h1 className="help-hero__title">How can we help you today?</h1>
            <p className="help-hero__subtitle">
              Find quick answers about bookings, payments and hosting — or reach a member of our team in one tap.
            </p>

            <form
              className="help-search"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
            >
              <button type="submit" className="help-search__icon" aria-label="Search help topics">
                <SearchIcon />
              </button>
              <input
                type="text"
                className="help-search__input"
                placeholder="Search for a topic, e.g. cancel a booking"
                aria-label="Search help topics"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {Boolean(query) && (
                <button type="button" className="help-search__clear" onClick={() => setQuery('')} aria-label="Clear search">
                  ×
                </button>
              )}
            </form>

            <div className="help-hero__suggestions">
              <span className="help-hero__suggestions-label">Popular</span>
              {SEARCH_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="help-chip help-chip--ghost"
                  onClick={() => applySearch(suggestion, false)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          <div className="help-hero__art">
            <img src={spaceyAvatar} alt="Spacey, the MillionSpaces assistant" className="help-hero__avatar" />
          </div>
        </div>
      </section>

      <section className="help-shell help-channels">
        {channels.map((channel) =>
          channel.href ? (
            <a key={channel.id} href={channel.href} className="help-channel">
              <span className="help-channel__icon">{channel.icon}</span>
              <h3 className="help-channel__title">{channel.title}</h3>
              <p className="help-channel__description">{channel.description}</p>
              <span className="help-channel__action">
                {channel.value}
                <ArrowIcon />
              </span>
            </a>
          ) : (
            <button key={channel.id} type="button" className="help-channel" onClick={channel.onClick}>
              <span className="help-channel__icon">{channel.icon}</span>
              <h3 className="help-channel__title">{channel.title}</h3>
              <p className="help-channel__description">{channel.description}</p>
              <span className="help-channel__action">
                {channel.value}
                <ArrowIcon />
              </span>
            </button>
          )
        )}
      </section>

      <section className="help-shell help-answers" ref={answersRef}>
        <header className="help-section-head">
          <h2 className="help-section-head__title">Browse common questions</h2>
          <p className="help-section-head__subtitle">Pick a topic to narrow things down.</p>
        </header>

        <div className="help-topics">
          {topicsWithCount.map((topic) => (
            <button
              key={topic.id}
              type="button"
              className={`help-chip ${activeTopic === topic.id ? 'help-chip--active' : ''}`}
              onClick={() => setActiveTopic(topic.id)}
            >
              {topic.label}
              <span className="help-chip__count">{topic.count}</span>
            </button>
          ))}
        </div>

        {visibleFaqs.length > 0 ? (
          <div className="help-faq">
            {visibleFaqs.map((faq) => {
              const isOpen = openQuestion === faq.question;
              return (
                <div key={faq.question} className={`help-faq__item ${isOpen ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="help-faq__question"
                    onClick={() => toggleQuestion(faq.question)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <span className="help-faq__toggle" aria-hidden="true" />
                  </button>
                  <div className="help-faq__answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="help-empty">
            <h3>{query.trim() ? `No results for “${query.trim()}”` : 'Nothing here yet'}</h3>
            <p>Try a different word, or let our team help you directly.</p>
            <div className="help-empty__actions">
              <button type="button" className="help-btn help-btn--ghost" onClick={resetFilters}>
                Clear search
              </button>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="help-btn help-btn--primary">
                Email support
              </a>
            </div>
          </div>
        )}

        <button type="button" className="help-faq__more" onClick={() => history.push(`/${country}/faq`)}>
          See all frequently asked questions
          <ArrowIcon />
        </button>
      </section>

      <section className="help-shell">
        <div className="help-cta">
          <div className="help-cta__copy">
            <h2>Still need a hand?</h2>
            <p>Our team knows every space on the platform and can help you find the right one.</p>
          </div>
          <div className="help-cta__actions">
            <a href={`tel:${SUPPORT_PHONE}`} className="help-btn help-btn--primary">
              <PhoneIcon />
              {SUPPORT_PHONE_LABEL}
            </a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="help-btn help-btn--outline">
              <MailIcon />
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpAndSupport;
